import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import { useLanguage } from '../../context/LanguageContext';
import { TOTAL_WIZARD_STEPS } from '../../constants/wizardSteps';

const PROFICIENCY_LEVELS = [
  { value: 'beginner', key: 'wizard.languageSkills.levels.beginner', label: 'Beginner (A1-A2)' },
  { value: 'intermediate', key: 'wizard.languageSkills.levels.intermediate', label: 'Intermediate (B1-B2)' },
  { value: 'advanced', key: 'wizard.languageSkills.levels.advanced', label: 'Advanced (C1)' },
  { value: 'native', key: 'wizard.languageSkills.levels.native', label: 'Native/Fluent (C2)' }
];

const IELTS_BANDS = [
  { value: '4.0-4.5', key: 'wizard.languageSkills.ielts.4_0_4_5', label: '4.0 - 4.5 (Limited)' },
  { value: '5.0-5.5', key: 'wizard.languageSkills.ielts.5_0_5_5', label: '5.0 - 5.5 (Modest)' },
  { value: '6.0-6.5', key: 'wizard.languageSkills.ielts.6_0_6_5', label: '6.0 - 6.5 (Competent)' },
  { value: '7.0-7.5', key: 'wizard.languageSkills.ielts.7_0_7_5', label: '7.0 - 7.5 (Good)' },
  { value: '8.0-8.5', key: 'wizard.languageSkills.ielts.8_0_8_5', label: '8.0 - 8.5 (Very Good)' },
  { value: '9.0', key: 'wizard.languageSkills.ielts.9_0', label: '9.0 (Expert)' }
];

const TOEFL_SCORES = [
  { value: '0-30', key: 'wizard.languageSkills.toefl.0_30', label: '0-30 (Limited)' },
  { value: '31-50', key: 'wizard.languageSkills.toefl.31_50', label: '31-50 (Low Intermediate)' },
  { value: '51-70', key: 'wizard.languageSkills.toefl.51_70', label: '51-70 (Intermediate)' },
  { value: '71-90', key: 'wizard.languageSkills.toefl.71_90', label: '71-90 (High Intermediate)' },
  { value: '91-100', key: 'wizard.languageSkills.toefl.91_100', label: '91-100 (Advanced)' },
  { value: '101-120', key: 'wizard.languageSkills.toefl.101_120', label: '101-120 (Expert)' }
];

/**
 * LanguageSkills component for collecting user's language proficiency
 * @param {Object} props - Component props
 * @param {number} props.stepNumber - Current step number in wizard flow
 * @param {string} props.title - Step title displayed to user
 * @param {string} props.description - Optional description text
 * @param {Function} props.onNext - Callback when user proceeds to next step
 * @param {Function} props.onPrevious - Callback when user goes to previous step
 * @param {boolean} props.canGoNext - Whether next button should be enabled
 * @param {boolean} props.canGoPrevious - Whether previous button should be enabled
 * @param {string} props.className - Additional CSS classes
 */
const LanguageSkills = ({
  stepNumber = 6,
  totalSteps = TOTAL_WIZARD_STEPS,
  title = '',
  description = '',
  onNext,
  onPrevious,
  canGoNext = false,
  canGoPrevious = true,
  className = ''
}) => {
  const { state, actions } = useWizard();
  const { t } = useLanguage();

  // Auto-scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);

  const defaultTitle = t('wizard.languageSkills.title', 'Language Skills');
  const defaultDescription = t('wizard.languageSkills.description', 'Language proficiency is essential for immigration and significantly impacts your eligibility for various programs.');
  const stepTitle = title || defaultTitle;
  const stepDescription = description || defaultDescription;
  const [formData, setFormData] = useState({
    englishReading: state.languageSkills.englishReading || '',
    englishWriting: state.languageSkills.englishWriting || '',
    englishSpeaking: state.languageSkills.englishSpeaking || '',
    englishListening: state.languageSkills.englishListening || '',
    hasIELTS: state.languageSkills.hasIELTS || '',
    ieltsScore: state.languageSkills.ieltsScore || '',
    hasTOEFL: state.languageSkills.hasTOEFL || '',
    toeflScore: state.languageSkills.toeflScore || '',
    otherLanguages: state.languageSkills.otherLanguages || ''
  });

  const [errors, setErrors] = useState({});

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.englishReading) {
      newErrors.englishReading = t('wizard.languageSkills.errors.englishReading.required', 'Please select your English reading level');
    }

    if (!formData.englishWriting) {
      newErrors.englishWriting = t('wizard.languageSkills.errors.englishWriting.required', 'Please select your English writing level');
    }

    if (!formData.englishSpeaking) {
      newErrors.englishSpeaking = t('wizard.languageSkills.errors.englishSpeaking.required', 'Please select your English speaking level');
    }

    if (!formData.englishListening) {
      newErrors.englishListening = t('wizard.languageSkills.errors.englishListening.required', 'Please select your English listening level');
    }

    if (!formData.hasIELTS) {
      newErrors.hasIELTS = t('wizard.languageSkills.errors.hasIELTS.required', 'Please specify if you have IELTS results');
    }

    if (formData.hasIELTS === 'yes' && !formData.ieltsScore) {
      newErrors.ieltsScore = t('wizard.languageSkills.errors.ieltsScore.required', 'Please select your IELTS score');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Reset score fields if test status changes
    if (field === 'hasIELTS' && value === 'no') {
      setFormData(prev => ({ ...prev, ieltsScore: '' }));
    }
    if (field === 'hasTOEFL' && value === 'no') {
      setFormData(prev => ({ ...prev, toeflScore: '' }));
    }
  }, [errors]);

  const handleNext = useCallback(() => {
    if (validateForm()) {
      // Update wizard state with language skills info
      actions.updateLanguageSkills(formData);

      if (onNext) {
        onNext({
          languageSkills: formData
        });
      }
    }
  }, [formData, validateForm, actions, onNext]);

  const canProceed = useMemo(() => {
    const requiredFields = ['englishReading', 'englishWriting', 'englishSpeaking', 'englishListening', 'hasIELTS'];
    const ieltsRequired = formData.hasIELTS === 'yes';

    return requiredFields.every(field => formData[field]) &&
           (!ieltsRequired || formData.ieltsScore) &&
           Object.keys(errors).every(field => !errors[field]);
  }, [formData, errors]);

  // Validate on mount to show any initial errors
  React.useEffect(() => {
    if (Object.keys(formData).some(key => formData[key])) {
      validateForm();
    }
  }, []);

  const calculateCLBLevel = useCallback(() => {
    if (!formData.hasIELTS || formData.hasIELTS === 'no') return null;

    const scoreMap = {
      '4.0-4.5': 4,
      '5.0-5.5': 5,
      '6.0-6.5': 6,
      '7.0-7.5': 7,
      '8.0-8.5': 8,
      '9.0': 9
    };

    return scoreMap[formData.ieltsScore] || null;
  }, [formData.hasIELTS, formData.ieltsScore]);

  const clbLevel = calculateCLBLevel();

  return (
    <div className={`language-skills-step w-full mx-auto ${className}`}>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="mb-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            {t('wizard.common.stepIndicator', 'Step {{current}} of {{total}}', {
              current: stepNumber,
              total: totalSteps
            })}
          </span>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">{stepTitle}</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">{stepDescription}</p>
      </div>

      {/* Form */}
      <div className="card p-6 sm:p-8">
        {/* English Proficiency Section */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">
            {t('wizard.languageSkills.section.englishProficiency.title', 'English Language Proficiency *')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Reading */}
            <div>
              <label htmlFor="englishReading" className="block text-sm font-medium text-gray-700 mb-2">
                {t('wizard.languageSkills.fields.englishReading.label', 'Reading')}
              </label>
              <select
                id="englishReading"
                value={formData.englishReading}
                onChange={(e) => handleInputChange('englishReading', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.englishReading ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{t('wizard.languageSkills.fields.englishReading.placeholder', 'Select reading level')}</option>
                {PROFICIENCY_LEVELS.map(level => (
                  <option key={level.value} value={level.value}>{t(level.key, level.label)}</option>
                ))}
              </select>
              {errors.englishReading && (
                <p className="mt-1 text-sm text-red-600">{errors.englishReading}</p>
              )}
            </div>

            {/* Writing */}
            <div>
              <label htmlFor="englishWriting" className="block text-sm font-medium text-gray-700 mb-2">
                {t('wizard.languageSkills.fields.englishWriting.label', 'Writing')}
              </label>
              <select
                id="englishWriting"
                value={formData.englishWriting}
                onChange={(e) => handleInputChange('englishWriting', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.englishWriting ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{t('wizard.languageSkills.fields.englishWriting.placeholder', 'Select writing level')}</option>
                {PROFICIENCY_LEVELS.map(level => (
                  <option key={level.value} value={level.value}>{t(level.key, level.label)}</option>
                ))}
              </select>
              {errors.englishWriting && (
                <p className="mt-1 text-sm text-red-600">{errors.englishWriting}</p>
              )}
            </div>

            {/* Speaking */}
            <div>
              <label htmlFor="englishSpeaking" className="block text-sm font-medium text-gray-700 mb-2">
                {t('wizard.languageSkills.fields.englishSpeaking.label', 'Speaking')}
              </label>
              <select
                id="englishSpeaking"
                value={formData.englishSpeaking}
                onChange={(e) => handleInputChange('englishSpeaking', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.englishSpeaking ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{t('wizard.languageSkills.fields.englishSpeaking.placeholder', 'Select speaking level')}</option>
                {PROFICIENCY_LEVELS.map(level => (
                  <option key={level.value} value={level.value}>{t(level.key, level.label)}</option>
                ))}
              </select>
              {errors.englishSpeaking && (
                <p className="mt-1 text-sm text-red-600">{errors.englishSpeaking}</p>
              )}
            </div>

            {/* Listening */}
            <div>
              <label htmlFor="englishListening" className="block text-sm font-medium text-gray-700 mb-2">
                {t('wizard.languageSkills.fields.englishListening.label', 'Listening')}
              </label>
              <select
                id="englishListening"
                value={formData.englishListening}
                onChange={(e) => handleInputChange('englishListening', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.englishListening ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{t('wizard.languageSkills.fields.englishListening.placeholder', 'Select listening level')}</option>
                {PROFICIENCY_LEVELS.map(level => (
                  <option key={level.value} value={level.value}>{t(level.key, level.label)}</option>
                ))}
              </select>
              {errors.englishListening && (
                <p className="mt-1 text-sm text-red-600">{errors.englishListening}</p>
              )}
            </div>
          </div>
        </div>

        {/* Standardized Test Scores */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">
            {t('wizard.languageSkills.section.tests.title', 'Standardized Test Scores')}
          </h3>

          {/* IELTS */}
          <div className="mb-6">
            <label htmlFor="hasIELTS" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.languageSkills.fields.hasIELTS.label', 'Do you have IELTS results? *')}
            </label>
            <select
              id="hasIELTS"
              value={formData.hasIELTS}
              onChange={(e) => handleInputChange('hasIELTS', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                errors.hasIELTS ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">{t('wizard.languageSkills.fields.hasIELTS.placeholder', 'Select IELTS status')}</option>
              <option value="yes">{t('wizard.languageSkills.fields.hasIELTS.options.yes', 'Yes, I have IELTS results')}</option>
              <option value="no">{t('wizard.languageSkills.fields.hasIELTS.options.no', "No, I haven't taken IELTS")}</option>
              <option value="planning">{t('wizard.languageSkills.fields.hasIELTS.options.planning', 'Planning to take it soon')}</option>
            </select>
            {errors.hasIELTS && (
              <p className="mt-1 text-sm text-red-600">{errors.hasIELTS}</p>
            )}

            {formData.hasIELTS === 'yes' && (
              <div className="mt-4">
                <label htmlFor="ieltsScore" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('wizard.languageSkills.fields.ieltsScore.label', 'IELTS Overall Band Score *')}
                </label>
                <select
                  id="ieltsScore"
                  value={formData.ieltsScore}
                  onChange={(e) => handleInputChange('ieltsScore', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    errors.ieltsScore ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">{t('wizard.languageSkills.fields.ieltsScore.placeholder', 'Select IELTS score')}</option>
                  {IELTS_BANDS.map(band => (
                    <option key={band.value} value={band.value}>{t(band.key, band.label)}</option>
                  ))}
                </select>
                {errors.ieltsScore && (
                  <p className="mt-1 text-sm text-red-600">{errors.ieltsScore}</p>
                )}
                {clbLevel && (
                  <p className="mt-2 text-sm text-green-600">
                    {t('wizard.languageSkills.fields.ieltsScore.clbHint', '💡 Equivalent to CLB Level {{level}} for Canadian immigration', { level: clbLevel })}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* TOEFL */}
          <div className="mb-6">
            <label htmlFor="hasTOEFL" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.languageSkills.fields.hasTOEFL.label', 'Do you have TOEFL results? (Optional)')}
            </label>
            <select
              id="hasTOEFL"
              value={formData.hasTOEFL}
              onChange={(e) => handleInputChange('hasTOEFL', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <option value="">{t('wizard.languageSkills.fields.hasTOEFL.placeholder', 'Select TOEFL status')}</option>
              <option value="yes">{t('wizard.languageSkills.fields.hasTOEFL.options.yes', 'Yes, I have TOEFL results')}</option>
              <option value="no">{t('wizard.languageSkills.fields.hasTOEFL.options.no', "No, I haven't taken TOEFL")}</option>
            </select>

            {formData.hasTOEFL === 'yes' && (
              <div className="mt-4">
                <label htmlFor="toeflScore" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('wizard.languageSkills.fields.toeflScore.label', 'TOEFL Total Score')}
                </label>
                <select
                  id="toeflScore"
                  value={formData.toeflScore}
                  onChange={(e) => handleInputChange('toeflScore', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                >
                  <option value="">{t('wizard.languageSkills.fields.toeflScore.placeholder', 'Select TOEFL score')}</option>
                  {TOEFL_SCORES.map(score => (
                    <option key={score.value} value={score.value}>{t(score.key, score.label)}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Other Languages */}
        <div>
          <label htmlFor="otherLanguages" className="block text-sm font-medium text-gray-700 mb-2">
            {t('wizard.languageSkills.fields.otherLanguages.label', 'Other Languages (Optional)')}
          </label>
          <textarea
            id="otherLanguages"
            value={formData.otherLanguages}
            onChange={(e) => handleInputChange('otherLanguages', e.target.value)}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            placeholder={t('wizard.languageSkills.fields.otherLanguages.placeholder', 'List any other languages you speak and your proficiency level')}
          />
          <p className="mt-1 text-sm text-gray-500">
            {t('wizard.languageSkills.fields.otherLanguages.helper', 'Include languages like French, German, Spanish, etc. which can be beneficial for immigration')}
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center mt-8">
        <button
          onClick={onPrevious}
          disabled={!canGoPrevious}
          className="btn btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('wizard.common.previous', 'Previous')}
        </button>

        <button
          onClick={handleNext}
          disabled={!canProceed}
          className="btn btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('wizard.common.next', 'Next Step')}
        </button>
      </div>
    </div>
  );
};

export default LanguageSkills;
